import * as React from 'react';

import { BaseParams } from '@common/typescript/objects/BaseParams';
import { FormikList, BaseFormikFieldListProps } from '@common/react/components/Forms/FormikList/FormikList';
import Autocomplete, { AutocompleteProps } from '@common/react/components/Forms/Autocomplete/Autocomplete';
import { getUserName, Named } from '@common/react/utils/utils';
import { WithDeleted } from '@common/typescript/objects/WithDeleted';

interface FormikFieldDoctorListProps<
	T extends WithDeleted & Named,
	TFromValues = BaseParams
> extends Omit<BaseFormikFieldListProps<T, TFromValues>, 'objectType' | 'objectId' | 'fieldName' | 'label'> {
	fieldName: string;
	label: string;
	renderItem: (item: T) => React.ReactNode;
	filterComponentProps?: Omit<AutocompleteProps<T>, 'type'>;
}

export const FormikFieldUserList = <
	T extends WithDeleted & Named,
	TFromValues = BaseParams
>(props: FormikFieldDoctorListProps<T, TFromValues>) => {
	const {
		fieldName,
		label,
		filterComponentProps,
		formikListProps,
		className,
		requestType = 'userList',
		renderItem,
	} = props;

	const {
		params,
		antdProps,
		...rest
	} = filterComponentProps ?? {};

	return <>
		<FormikList<T, TFromValues>
			fieldName={fieldName}
			label={label}
			className={className}
			equalityChecker={(item, newItem) => item.id === newItem.id}
			renderItem={(user, removeItem) => (
				<div className="inline-select-item" key={user.id}>
					<i
						className="fa fa-times inline-select-item__remove"
						onClick={removeItem}
					/>
					<span>{renderItem(user)}</span>
				</div>
			)}
			renderInput={(addNewItem) =>
				<Autocomplete<T>
					type={requestType}
					renderOption={(autocompleteItem) => {
						return {
							key: `${autocompleteItem.id}`,
							value: getUserName(autocompleteItem),
							title: getUserName(autocompleteItem),
							item: autocompleteItem,
							label: renderItem(autocompleteItem),
						};
					}}
					onSelect={(value, option) => {
						addNewItem(option.props.item);
					}}
					loadOnFocus
					value=""
					isClear
					params={params}
					placeholder="Start typing for search..."
					antdProps={{
						id: fieldName,
						...antdProps,
					}}
					{...rest}
				/>
			}
			{...formikListProps}
		/>
	</>;
};
